import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, FileText, Send, CheckCircle2, XCircle, Loader2, Eye, Rocket,
  ClipboardList, Inbox, LayoutTemplate, Plus, ChevronLeft, Upload, Link2, Copy,
  Clock, Globe, ListChecks,
} from "lucide-react";

const SLA_TINT = {
  ON_TRACK: "bg-emerald-50 text-emerald-700", DUE_SOON: "bg-amber-50 text-amber-700",
  BREACHED: "bg-rose-50 text-rose-700", MET: "bg-emerald-50 text-emerald-700",
  MISSED: "bg-rose-50 text-rose-700",
};
const SlaBadge = ({ sla }) => {
  if (!sla) return null;
  const label = { ON_TRACK: "On track", DUE_SOON: "Due soon", BREACHED: "SLA breached",
    MET: "SLA met", MISSED: "SLA missed" }[sla.state] || sla.state;
  return (
    <span data-testid="sla-badge" className={"inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 " + (SLA_TINT[sla.state] || "bg-slate-100 text-slate-600")}>
      <Clock className="h-3 w-3" />{label}{sla.remaining_hours != null && sla.state !== "BREACHED" ? ` · ${sla.remaining_hours}h` : ""}
    </span>
  );
};

const STATUS_TINT = {
  DRAFT: "bg-slate-100 text-slate-600", PUBLISHED: "bg-emerald-50 text-emerald-700",
  INACTIVE: "bg-amber-50 text-amber-700", "AWAITING APPROVAL": "bg-sky-50 text-sky-700",
  APPROVED: "bg-emerald-50 text-emerald-700", REJECTED: "bg-rose-50 text-rose-700",
  SUBMITTED: "bg-sky-50 text-sky-700",
};

const TABS = [
  { id: "mine", label: "My Forms", icon: FileText },
  { id: "templates", label: "Templates", icon: LayoutTemplate },
  { id: "approvals", label: "Approvals", icon: Inbox },
];

const isVisible = (f, values) => {
  const c = f.conditional;
  if (!c) return true;
  return String(values[c.field]) === String(c.equals);
};

export default function OneForms() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("mine");
  const [data, setData] = useState({ items: [], templates: [] });
  const [approvals, setApprovals] = useState([]);
  const [view, setView] = useState("list");       // list | ai | preview | fill | responses
  const [busy, setBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [current, setCurrent] = useState(null);    // form being previewed/filled
  const [values, setValues] = useState({});
  const [pubLink, setPubLink] = useState(null);    // { enabled, token, url }
  const [respItems, setRespItems] = useState([]);
  const [slaHours, setSlaHours] = useState(24);

  const load = useCallback(async () => {
    const { data } = await api.get("/one-forms/forms", { params: { tab: "all" } });
    setData(data);
    const a = await api.get("/one-forms/my-approvals").catch(() => ({ data: { items: [] } }));
    setApprovals(a.data.items || []);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (params.get("create") === "ai") { setView("ai"); setParams({}); } }, [params, setParams]);

  const generateAI = async () => {
    if (!aiPrompt.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/one-forms/forms/ai-generate", { prompt: aiPrompt, save: true });
      toast.success("Form drafted by AI");
      setCurrent(data.saved); setView("preview"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "AI could not build the form"); }
    finally { setBusy(false); }
  };

  const applyTemplate = async (key, name) => {
    setBusy(true);
    try {
      const { data } = await api.post("/one-forms/forms", { template_key: key, name });
      toast.success("Form created from template");
      setCurrent(data); setView("preview"); await load();
    } catch { toast.error("Could not create from template"); }
    finally { setBusy(false); }
  };

  const openForm = async (id, mode) => {
    const { data } = await api.get(`/one-forms/forms/${id}`);
    setCurrent(data); setValues({}); setView(mode);
    setSlaHours(data.sla?.hours || 24);
    setPubLink(null);
    if (mode === "preview" && data.status === "PUBLISHED") {
      api.get(`/one-forms/forms/${id}/public-link`).then((r) => setPubLink(r.data)).catch(() => {});
    }
  };

  const importDoc = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/one-forms/forms/import-document?save=true", fd);
      toast.success("Form imported from document");
      setCurrent(data.saved); setView("preview"); setSlaHours(data.saved.sla?.hours || 24);
      setPubLink(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the document"); }
    finally { setBusy(false); }
  };

  const saveSla = async (enabled) => {
    try {
      const { data } = await api.put(`/one-forms/forms/${current.id}`, {
        sla: { enabled, hours: Number(slaHours) || 24 },
      });
      setCurrent(data);
      toast.success(enabled ? "SLA target enabled" : "SLA target turned off");
    } catch { toast.error("Could not save SLA"); }
  };

  const genPublicLink = async (regenerate = false) => {
    try {
      const { data } = await api.post(`/one-forms/forms/${current.id}/public-link`, null, { params: { regenerate } });
      setPubLink(data); toast.success(regenerate ? "New link generated" : "Public link ready");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create link"); }
  };

  const revokePublicLink = async () => {
    try {
      await api.post(`/one-forms/forms/${current.id}/public-link/revoke`);
      setPubLink({ enabled: false, token: pubLink?.token, url: pubLink?.url });
      toast.success("Public link disabled");
    } catch { toast.error("Could not revoke link"); }
  };

  const publicUrl = (u) => (u && u.startsWith("http")) ? u : `${window.location.origin}${u || ""}`;
  const copyLink = () => {
    navigator.clipboard?.writeText(publicUrl(pubLink?.url));
    toast.success("Link copied");
  };

  const openResponses = async () => {
    try {
      const { data } = await api.get(`/one-forms/forms/${current.id}/responses`);
      setRespItems(data.items || []); setView("responses");
    } catch { toast.error("Could not load responses"); }
  };

  const publish = async () => {
    try { await api.post(`/one-forms/forms/${current.id}/publish`); toast.success("Form published");
      await openForm(current.id, "preview"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/one-forms/forms/${current.id}/submit`, { values });
      toast.success(`Submitted — ${data.request_no} (${data.status})`);
      setView("list"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  const decide = async (reqId, decision) => {
    try { await api.post(`/one-forms/requests/${reqId}/decision`, { decision });
      toast.success(decision === "APPROVE" ? "Approved" : "Rejected"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const renderField = (f) => {
    if (!isVisible(f, values)) return null;
    const v = values[f.key] ?? "";
    const set = (val) => setValues((s) => ({ ...s, [f.key]: val }));
    const common = { "data-testid": `field-${f.key}`, value: v, onChange: (e) => set(e.target.value) };
    return (
      <div key={f.key} className="space-y-1">
        <label className="text-[12px] font-medium text-slate-700">
          {f.label}{f.required && <span className="text-rose-500"> *</span>}
        </label>
        {f.type === "long_text" ? <Textarea {...common} className="text-sm" />
          : f.type === "yesno" ? (
            <div className="flex gap-2">
              {["Yes", "No"].map((o) => (
                <button key={o} data-testid={`field-${f.key}-${o.toLowerCase()}`} onClick={() => set(o)}
                  className={"px-3 py-1 rounded-md text-[12px] border " + (v === o ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200")}>{o}</button>
              ))}
            </div>)
          : ["dropdown", "radio"].includes(f.type) && f.options ? (
            <select {...common} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
              <option value="">Select…</option>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>)
          : f.type === "multiselect" && f.options ? (
            <div className="flex flex-wrap gap-1.5">
              {f.options.map((o) => {
                const arr = Array.isArray(v) ? v : [];
                const on = arr.includes(o);
                return <button key={o} onClick={() => set(on ? arr.filter((x) => x !== o) : [...arr, o])}
                  className={"px-2.5 py-1 rounded-full text-[11px] border " + (on ? "bg-sky-600 text-white border-sky-600" : "bg-white border-slate-200")}>{o}</button>;
              })}
            </div>)
          : <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} {...common} className="h-9 text-sm" />}
      </div>
    );
  };

  // ---------- BUILDER PREVIEW ----------
  if (view === "preview" && current) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <button onClick={() => setView("list")} className="flex items-center gap-1 text-[12px] text-slate-500"><ChevronLeft className="h-3.5 w-3.5" /> Back to forms</button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{current.name}</h1>
            <p className="text-sm text-slate-500">{current.purpose}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={STATUS_TINT[current.status]}>{current.status}</Badge>
            {current.status === "PUBLISHED" && (
              <Button size="sm" variant="outline" onClick={openResponses} data-testid="view-responses-btn"><ListChecks className="h-3.5 w-3.5 mr-1" />Responses ({current.submissions || 0})</Button>
            )}
            {current.status !== "PUBLISHED"
              ? <Button size="sm" onClick={publish} data-testid="publish-form-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white"><Rocket className="h-3.5 w-3.5 mr-1" />Publish</Button>
              : <Button size="sm" onClick={() => openForm(current.id, "fill")} data-testid="fill-form-btn"><Send className="h-3.5 w-3.5 mr-1" />Fill & submit</Button>}
          </div>
        </div>
        {(current.approval?.required) && (
          <div className="text-[12px] text-sky-700 bg-sky-50 rounded-md px-3 py-1.5">
            Approval: {current.approval.mode} — {(current.approval.stages || []).map((s) => s.name).join(" → ")}
          </div>
        )}

        {/* SLA target */}
        <div className="rounded-lg border border-slate-200 p-4 space-y-2" data-testid="sla-config">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800"><Clock className="h-4 w-4 text-sky-600" />Service-level target (SLA)</div>
            {current.sla?.enabled ? <Badge className="bg-emerald-50 text-emerald-700">On · {current.sla.hours}h</Badge> : <Badge className="bg-slate-100 text-slate-500">Off</Badge>}
          </div>
          <p className="text-[12px] text-slate-500">Track a resolution deadline on every submitted request. Requests show On-track / Due-soon / Breached badges.</p>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-slate-600">Resolve within</span>
            <Input type="number" min={1} value={slaHours} onChange={(e) => setSlaHours(e.target.value)} data-testid="sla-hours" className="h-8 w-20 text-sm" />
            <span className="text-[12px] text-slate-600">hours</span>
            <Button size="sm" onClick={() => saveSla(true)} data-testid="sla-save" className="bg-sky-600 hover:bg-sky-700 text-white">Save target</Button>
            {current.sla?.enabled && <Button size="sm" variant="outline" onClick={() => saveSla(false)} data-testid="sla-off">Turn off</Button>}
          </div>
        </div>

        {/* Public share */}
        {current.status === "PUBLISHED" && (
          <div className="rounded-lg border border-slate-200 p-4 space-y-2" data-testid="public-share">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800"><Globe className="h-4 w-4 text-sky-600" />Public form link</div>
              {pubLink?.enabled ? <Badge className="bg-emerald-50 text-emerald-700">Live</Badge> : <Badge className="bg-slate-100 text-slate-500">Not shared</Badge>}
            </div>
            <p className="text-[12px] text-slate-500">Share this form with people outside the platform — no login needed. Submissions arrive as governed requests.</p>
            {pubLink?.enabled ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input readOnly value={publicUrl(pubLink.url)} data-testid="public-link-url" className="h-8 text-[12px]" />
                  <Button size="sm" variant="outline" onClick={copyLink} data-testid="public-link-copy"><Copy className="h-3.5 w-3.5" /></Button>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => genPublicLink(true)} data-testid="public-link-regen">Generate new link</Button>
                  <Button size="sm" variant="outline" onClick={revokePublicLink} data-testid="public-link-revoke" className="text-rose-600">Disable</Button>
                </div>
              </div>
            ) : (
              <Button size="sm" onClick={() => genPublicLink(false)} data-testid="public-link-create" className="bg-sky-600 hover:bg-sky-700 text-white"><Link2 className="h-3.5 w-3.5 mr-1" />Get public link</Button>
            )}
          </div>
        )}

        {(current.sections || []).map((sec, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-3">
            <div className="text-[13px] font-semibold text-slate-800">{sec.title}</div>
            {(sec.fields || []).map((f) => (
              <div key={f.key} className="flex items-center justify-between text-sm border-b border-slate-50 py-1.5">
                <span className="text-slate-700">{f.label}{f.required && <span className="text-rose-500"> *</span>}</span>
                <span className="text-[11px] text-slate-400">{f.type}{f.conditional ? " · conditional" : ""}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // ---------- FILL / SUBMIT ----------
  if (view === "fill" && current) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4">
        <button onClick={() => setView("list")} className="flex items-center gap-1 text-[12px] text-slate-500"><ChevronLeft className="h-3.5 w-3.5" /> Cancel</button>
        <h1 className="text-xl font-semibold text-slate-900">{current.name}</h1>
        {(current.sections || []).map((sec, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-3">
            <div className="text-[13px] font-semibold text-slate-800">{sec.title}</div>
            {(sec.fields || []).map(renderField)}
          </div>
        ))}
        <Button onClick={submit} disabled={busy} data-testid="submit-request-btn" className="w-full bg-sky-600 hover:bg-sky-700 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Submit request
        </Button>
      </div>
    );
  }

  // ---------- RESPONSES ----------
  if (view === "responses" && current) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4" data-testid="responses-view">
        <button onClick={() => setView("preview")} className="flex items-center gap-1 text-[12px] text-slate-500"><ChevronLeft className="h-3.5 w-3.5" /> Back to form</button>
        <h1 className="text-xl font-semibold text-slate-900">{current.name} — Responses</h1>
        {respItems.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No responses yet.</div>}
        {respItems.map((r) => (
          <div key={r.id} data-testid={`response-${r.id}`} className="rounded-lg border border-slate-200 px-4 py-3 space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-slate-800">{r.request_no}</span>
              <Badge className={STATUS_TINT[r.status]}>{r.status}</Badge>
              {r.source === "public" && <Badge className="bg-violet-50 text-violet-700"><Globe className="h-3 w-3 mr-1" />Public</Badge>}
              <SlaBadge sla={r.sla} />
              <span className="ml-auto text-[11px] text-slate-400">by {r.submitter}</span>
            </div>
            <div className="text-[12px] text-slate-500">{new Date(r.created_at).toLocaleString()}{r.current_stage ? ` · Stage: ${r.current_stage}` : ""}</div>
          </div>
        ))}
      </div>
    );
  }

  // ---------- CREATE WITH AI ----------
  if (view === "ai") {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4">
        <button onClick={() => setView("list")} className="flex items-center gap-1 text-[12px] text-slate-500"><ChevronLeft className="h-3.5 w-3.5" /> Back</button>
        <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-sky-600" /><h1 className="text-xl font-semibold text-slate-900">Create with AI</h1></div>
        <p className="text-sm text-slate-500">Describe the process in plain language — ONE builds the form, fields, conditions and approvals for you to review.</p>
        <Textarea data-testid="ai-prompt" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={5}
          placeholder='e.g. "Create a New Joiner request form with employee name, email, department, manager, joining date, laptop requirement and Microsoft licence. Manager and IT approval required."'
          className="text-sm" />
        <Button onClick={generateAI} disabled={busy} data-testid="ai-generate-btn" className="bg-sky-600 hover:bg-sky-700 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Build my form
        </Button>
      </div>
    );
  }

  // ---------- LIST ----------
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5" data-testid="one-forms-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><ClipboardList className="h-6 w-6 text-sky-600" />ONE Forms</h1>
          <p className="text-sm text-slate-500">No-code forms & automated request workflows.</p>
        </div>
        <div className="flex items-center gap-2">
          <label data-testid="import-document-btn" className="cursor-pointer inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Import document
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={importDoc} className="hidden" data-testid="import-document-input" disabled={busy} />
          </label>
          <Button onClick={() => setView("ai")} data-testid="create-with-ai-btn" className="bg-sky-600 hover:bg-sky-700 text-white"><Sparkles className="h-4 w-4 mr-1.5" />Create with AI</Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={"flex items-center gap-1.5 px-3 py-2 text-[13px] border-b-2 -mb-px " + (tab === t.id ? "border-sky-600 text-sky-700 font-medium" : "border-transparent text-slate-500")}>
            <t.icon className="h-3.5 w-3.5" />{t.label}
            {t.id === "approvals" && approvals.length > 0 && <span className="ml-1 text-[10px] bg-rose-500 text-white rounded-full px-1.5">{approvals.length}</span>}
          </button>
        ))}
      </div>

      {tab === "mine" && (
        <div className="space-y-2">
          {data.items.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No forms yet. <button className="text-sky-600" onClick={() => setView("ai")}>Create one with AI</button>.</div>}
          {data.items.map((f) => (
            <div key={f.id} data-testid={`form-row-${f.id}`} className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 hover:bg-slate-50">
              <FileText className="h-4 w-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{f.name}</div>
                <div className="text-[12px] text-slate-500 truncate">{f.purpose}</div>
              </div>
              <span className="text-[11px] text-slate-400">{f.submissions || 0} responses</span>
              <Badge className={STATUS_TINT[f.status]}>{f.status}</Badge>
              <Button size="sm" variant="outline" onClick={() => openForm(f.id, "preview")} data-testid={`open-form-${f.id}`}><Eye className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}

      {tab === "templates" && (
        <div className="grid sm:grid-cols-2 gap-3">
          {data.templates.map((t) => (
            <div key={t.key} data-testid={`template-${t.key}`} className="rounded-lg border border-slate-200 p-4 space-y-2">
              <div className="text-sm font-semibold text-slate-800">{t.name}</div>
              <div className="text-[12px] text-slate-500">{t.purpose}</div>
              <Button size="sm" variant="outline" onClick={() => applyTemplate(t.key, t.name)} data-testid={`use-template-${t.key}`}><Plus className="h-3.5 w-3.5 mr-1" />Use template</Button>
            </div>
          ))}
        </div>
      )}

      {tab === "approvals" && (
        <div className="space-y-2">
          {approvals.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No pending approvals.</div>}
          {approvals.map((r) => (
            <div key={r.id} data-testid={`approval-${r.id}`} className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800">{r.form_name} <span className="text-[11px] text-slate-400">· {r.request_no}</span></div>
                <div className="text-[12px] text-slate-500 flex items-center gap-2">Stage: {r.current_stage} · by {r.submitter} <SlaBadge sla={r.sla} /></div>
              </div>
              <Button size="sm" onClick={() => decide(r.id, "APPROVE")} data-testid={`approve-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve</Button>
              <Button size="sm" variant="outline" onClick={() => decide(r.id, "REJECT")} data-testid={`reject-${r.id}`} className="text-rose-600"><XCircle className="h-3.5 w-3.5 mr-1" />Reject</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
